import { create } from 'zustand';
import { cleanInterpretation, type DrawnCard, type Mbti, type RoomState } from '@tarot/shared';
import { RoomSocket } from '../../lib/ws';
import { roomAlive } from '../../lib/api';
import { recallSeat, rememberSeat } from './seat';
import { TOSS_EVENTS, tossLog } from '../../lib/toss';

/**
 * 방 실시간 상태 — RoomDO의 ServerMessage를 reduce (설계서 §7).
 * 서버 스냅샷(room:state)이 진실의 원천이고, card:revealed/ai:delta는 증분 반영.
 */

/**
 * 'reconnecting'은 **끊겼지만 아직 포기하지 않은** 상태다.
 * 이때 화면을 비우면 안 된다 — 모바일에서 앱 전환만 해도 수시로 지나가는 구간이라,
 * 방 내용을 그대로 두고 위에 안내만 얹는다. 'closed'는 재시도를 다 쓴 뒤에만 온다.
 *
 * 'gone'은 **방 자체가 없어진** 상태(만료·정리). 재연결로 될 일이 아니므로 즉시 멈추고
 * 새 방을 만들도록 안내한다 — 붙을 곳이 없는데 스피너만 도는 게 제일 나쁘다.
 */
type ConnStatus = 'idle' | 'connecting' | 'reconnecting' | 'joined' | 'closed' | 'gone';

/**
 * 재촉 벨 진동 패턴 (짧게-쉬고-짧게, ms).
 * Vibration API는 **앱/PWA 설치 없이 일반 브라우저에서 동작**한다 (안드로이드 크롬 등).
 * 방에 들어와 있는 동안은 페이지가 열려 있으므로 재촉 알림에 그대로 쓸 수 있다.
 * iOS 사파리는 미지원 — 지원 안 하면 조용히 무시된다.
 */
const NUDGE_VIBRATION = [120, 60, 120];

function buzz() {
  try {
    navigator.vibrate?.(NUDGE_VIBRATION);
  } catch {
    /* 미지원 브라우저 — 토스트만 표시 */
  }
}

/** 이 시간 안에 좌석 배정을 못 받으면 실패 처리 (없어진 방·서버 오류 → 무한 로딩 방지) */
const CONNECT_TIMEOUT_MS = 8000;
/** 재촉 알림 토스트 표시 시간 */
const NUDGE_TOAST_MS = 4000;
/**
 * 상대 카드 공개 토스트 표시 시간.
 * 재촉보다 짧게 — 여러 장이 연달아 공개되므로 오래 남으면 화면을 가린다.
 */
const REVEAL_TOAST_MS = 2200;

interface RoomStore {
  status: ConnStatus;
  state: RoomState | null;
  mySeat: number | null;
  aiText: string;
  /** 해석에 쓰인 토큰 수 — 결과 저장 시 함께 보내 리딩별 기록에 남긴다 (ai:done에서 수신) */
  aiUsage: { tokensIn?: number; tokensOut?: number };
  errorMsg: string | null;
  /** 재촉 받음 — 토스트 표시용 (NUDGE_TOAST_MS 후 자동 소멸) */
  nudgeFrom: string | null;
  /**
   * 방금 **남이** 공개한 카드 — 토스트로 잠깐 띄운다 (REVEAL_TOAST_MS 후 자동 소멸).
   *
   * 모바일에서는 상대 카드 영역이 접힘 아래로 밀려(390×740에서 889px 지점) 카드가
   * 실시간으로 공개되는 걸 아예 모르고 지나간다. 이 서비스의 핵심이 "함께 본다"인데
   * 그게 안 보이면 혼자 하는 것과 구분이 안 된다.
   */
  reveal: { nickname: string; card: DrawnCard } | null;
  socket: RoomSocket | null;

  join: (roomId: string, nickname: string, mbti?: Mbti) => void;
  start: () => void;
  pick: (card: DrawnCard) => void;
  sendNudge: (toSeat: number) => void;
  clearError: () => void;
  leave: () => void;
}

export const useRoomStore = create<RoomStore>((set, get) => {
  let nudgeTimer: ReturnType<typeof setTimeout> | undefined;
  let revealTimer: ReturnType<typeof setTimeout> | undefined;

  return {
    status: 'idle',
    state: null,
    mySeat: null,
    aiText: '',
    aiUsage: {},
    errorMsg: null,
    nudgeFrom: null,
    reveal: null,
    socket: null,

    join(roomId, nickname, mbti) {
      get().socket?.close();

      /**
       * 방이 없어졌으면 재연결을 멈춘다.
       * 소켓 실패만으로는 "방 만료"와 "잠깐 끊김"을 구분할 수 없다 — 브라우저 WebSocket은
       * 업그레이드 응답의 상태 코드를 알려주지 않는다. 그래서 HTTP로 한 번 물어본다.
       * 확인 자체가 실패하면(null) 아무것도 하지 않는다 — 오프라인일 때 멀쩡한 방을 버리면 안 된다.
       */
      const stopIfGone = async () => {
        // true = 살아 있음, null = 확인 실패 → 둘 다 계속 시도한다. false 일 때만 멈춘다
        if ((await roomAlive(roomId)) !== false) return;
        socket.close();
        set({ status: 'gone' });
      };

      // 방이 사라졌거나 서버 오류로 연결이 안 되면 타임아웃 후 closed 처리
      const timeout = setTimeout(() => {
        if (get().status === 'connecting') {
          socket.close();
          set({ status: 'closed' });
        }
      }, CONNECT_TIMEOUT_MS);
      const socket = new RoomSocket(roomId, {
        /**
         * 최초 연결뿐 아니라 **재연결마다** 불린다. 매번 좌석 증명(token)을 실어 보내면
         * 서버가 원래 자리를 돌려주고, 죽은 채 남아 있던 이전 소켓을 정리해 준다.
         */
        onOpen: () => {
          // 매번 새로 읽는다 — 첫 입장 때 받은 토큰이 그 뒤 재연결부터 쓰인다
          const token = recallSeat(roomId)?.token;
          socket.send({ type: 'seat:join', nickname, mbti, ...(token ? { token } : {}) });
        },
        onReconnecting: (attempt) => {
          clearTimeout(timeout);
          // 이미 앉아 본 적이 있으면 방 내용을 유지한 채 안내만 — 아니면 평소대로 연결 중
          set((s) => ({ status: s.mySeat === null ? 'connecting' : 'reconnecting' }));
          // 끊김이 시작될 때 한 번만 확인한다. 매 시도마다 부르면 요청만 늘고 얻는 게 없다
          // (화면으로 돌아왔을 때도 attempt가 1부터 다시 시작하므로 그때 또 확인된다)
          if (attempt === 1) void stopIfGone();
        },
        onGaveUp: () => {
          clearTimeout(timeout);
          set({ status: 'closed' });
          void stopIfGone();
        },
        onMessage: (msg) => {
          switch (msg.type) {
            case 'room:state':
              set({
                state: msg.state,
                ...(msg.state.interpretation ? { aiText: msg.state.interpretation } : {}),
              });
              break;
            case 'seat:assigned':
              clearTimeout(timeout);
              // 좌석 증명까지 함께 기억해 둔다 — 다음 재접속에서 이 자리를 되찾는 열쇠
              rememberSeat(roomId, nickname, mbti, msg.token);
              socket.arm(); // 자리를 받았으니, 이후 끊김은 스스로 복구한다
              // 첫 착석에서만 — 재연결 때도 seat:assigned가 오는데(좌석 복구) 그때는
              // mySeat이 이미 있으므로 전환이 부풀지 않는다
              if (get().mySeat === null) tossLog(TOSS_EVENTS.roomEnter);
              set({ mySeat: msg.seat, status: 'joined' });
              break;
            case 'card:revealed':
              set((s) => {
                if (!s.state) return {};
                const state = structuredClone(s.state);
                const p = state.participants.find((x) => x.seat === msg.seat);
                if (p && !p.cards.some((c) => c.position === msg.card.position))
                  p.cards.push(msg.card);
                // 내가 뽑은 건 이미 눈앞에서 봤다 — 남이 뽑은 것만 알린다
                if (msg.seat === s.mySeat || !p) return { state };
                clearTimeout(revealTimer);
                revealTimer = setTimeout(() => set({ reveal: null }), REVEAL_TOAST_MS);
                return { state, reveal: { nickname: p.nickname, card: msg.card } };
              });
              break;
            case 'nudged':
              clearTimeout(nudgeTimer);
              set({ nudgeFrom: msg.fromNickname });
              buzz(); // 화면을 안 보고 있어도 알아채도록 진동
              nudgeTimer = setTimeout(() => set({ nudgeFrom: null }), NUDGE_TOAST_MS);
              break;
            case 'ai:delta':
              set((s) => ({ aiText: s.aiText + msg.text }));
              break;
            case 'room:error':
              // 방이 만료돼 서버가 정리한 경우 — 재연결로 될 일이 아니므로 여기서 끝낸다
              if (msg.code === 'expired') {
                clearTimeout(timeout);
                socket.close();
                set({ status: 'gone' });
                break;
              }
              // 입장 단계 에러(중복 닉네임·정원 초과 등) → 즉시 폼으로 복귀.
              // 재연결 중이라면 이미 앉았던 사람이므로 폼으로 되돌리지 않는다 (아래 분기)
              if (get().status === 'connecting') {
                clearTimeout(timeout);
                socket.close();
                set({ errorMsg: msg.message, status: 'idle' });
              } else {
                set({ errorMsg: msg.message });
              }
              break;
            case 'ai:done':
              // 끝난 뒤 한 번 정리 — 모델이 문장 끝에 잡음 토큰을 붙여 보내는 일이 있다
              set((s) => ({
                aiText: cleanInterpretation(s.aiText),
                aiUsage: {
                  ...(msg.tokensIn !== undefined ? { tokensIn: msg.tokensIn } : {}),
                  ...(msg.tokensOut !== undefined ? { tokensOut: msg.tokensOut } : {}),
                },
              }));
              break;
            case 'pong':
              break;
          }
        },
      });
      set({
        socket,
        status: 'connecting',
        state: null,
        mySeat: null,
        aiText: '',
        aiUsage: {},
        errorMsg: null,
      });
      socket.connect();
    },

    start: () => get().socket?.send({ type: 'room:start' }),
    pick: (card) => get().socket?.send({ type: 'card:pick', card }),
    sendNudge: (toSeat) => get().socket?.send({ type: 'nudge', toSeat }),
    clearError: () => set({ errorMsg: null }),

    leave() {
      get().socket?.close();
      clearTimeout(nudgeTimer);
      clearTimeout(revealTimer);
      set({
        socket: null,
        status: 'idle',
        state: null,
        mySeat: null,
        aiText: '',
        aiUsage: {},
        errorMsg: null,
        nudgeFrom: null,
        reveal: null,
      });
    },
  };
});
